import React, { useState } from "react";
import { IoBagHandleOutline } from "react-icons/io5";
import objectiveProductIcon from "../../assets/images/icons/ads-objective-product.svg";
import objectiveMarketplaceIcon from "../../assets/images/icons/ads-objective-marketplace.svg";
import { promoteListing, createAdSetTargeting } from "./usePromoteListingApi";

const OBJECTIVE_LABELS = {
  product_listing_views: { label: "Product Listing Views", iconSrc: objectiveProductIcon },
  marketplace_conversions: { label: "Marketplace Conversions", iconSrc: objectiveMarketplaceIcon },
};

const PLACEMENT_LABELS = {
  searchResult: "Search Result",
  marketplaceFeed: "Marketplace Feed",
  categoryResults: "Category Results",
};

// Our UI keys -> the backend's real allowed placement values (configuration/ad-targeting.js)
const PLACEMENT_BACKEND_VALUES = {
  searchResult: "search_result",
  marketplaceFeed: "marketplace_feed",
  categoryResults: "category_results",
};

const daysBetween = (start, end) =>
  start && end ? Math.max(1, Math.ceil((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24))) : null;

const ChevronButton = ({ open, onClick }) => (
  <button type="button" onClick={onClick} className="w-6 h-6 flex items-center justify-center shrink-0">
    <svg
      width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#095228" strokeWidth="2.5"
      style={{ transform: open ? "rotate(0deg)" : "rotate(180deg)" }}
    >
      <path d="M18 15l-6-6-6 6" />
    </svg>
  </button>
);

export default function PromoteFinalPreview({ product, formData, onBack, onSubmitted }) {
  const [targetingOpen, setTargetingOpen] = useState(true);
  const [budgetOpen, setBudgetOpen] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const objective = OBJECTIVE_LABELS[formData.objective];
  const productImage = product?.product_media?.[0]?.url || "/image/default.png";
  const productImages = product?.product_media?.length ? product.product_media : [{ url: productImage }];

  const days = daysBetween(formData.startDate, formData.endDate) || 1;
  const isDaily = formData.budgetType === "daily";
  const dailyBudget = isDaily ? Number(formData.budget || 0) : Number(formData.budget || 0) / days;
  const totalBudget = isDaily ? Number(formData.budget || 0) * days : Number(formData.budget || 0);

  const selectedPlacements = Object.entries(formData.placements)
    .filter(([, checked]) => checked)
    .map(([key]) => PLACEMENT_LABELS[key]);

  // promoteListing creates the campaign/ad set/ad and now also sets delivery_config
  // (maximum_bid + placements) on the ad, which is what the buyer-facing auction engine
  // actually reads for eligibility - so placements go in this call too, not just the
  // display-layer createAdSetTargeting call below.
  const handleSubmit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const selectedBackendPlacements = Object.entries(formData.placements)
        .filter(([, checked]) => checked)
        .map(([key]) => PLACEMENT_BACKEND_VALUES[key]);

      const result = await promoteListing({
        product_id: product.id,
        campaign_name: formData.campaignName,
        budget: totalBudget,
        duration_days: days,
        objective: formData.objective,
        ad_set_name: formData.adSetName,
        placements: selectedBackendPlacements,
      });

      try {
        await createAdSetTargeting(result.adSet.id, {
          min_age: formData.minAge,
          max_age: formData.maxAge,
          genders: [formData.gender],
          // Unlike genders, the backend's device list has no "all" value - omitting the field
          // entirely means "no device restriction", which is what "All" is meant to convey.
          ...(formData.devices !== "all" && { devices: [formData.devices] }),
          interest_ids: formData.interests.map((i) => i.id),
          seller_relationship: { followers: formData.connectToSeller },
          previous_brand_buyers: formData.previousBrandBuyers,
        });
      } catch (targetingErr) {
        // The campaign/ad set/ad already exist at this point - don't block success over the
        // targeting save failing, just carry the message through so it can be surfaced.
        result.targetingError = targetingErr.response?.data?.message || "Failed to save targeting";
      }

      onSubmitted(result);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to submit ad campaign");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Feeds preview - matches the real marketplace card (ShopCardList.js's ProductCard) */}
        <div>
          <p className="text-sm font-semibold text-black mb-2">Feeds</p>
          <div className="relative bg-[#FAFAFA] rounded-lg shadow-sm">
            <div className="w-full h-[170px]">
              <img src={productImage} alt={product?.product_name} className="w-full h-full object-contain rounded-t-lg" />
            </div>
            {product?.offer_percentage ? (
              <span className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded">
                {product.offer_percentage}% Offer
              </span>
            ) : null}
            <span className="absolute top-2 right-2 bg-[#ff8a3d] text-white text-xs font-bold px-2 py-1 rounded">
              Sponsored
            </span>
            <div className="p-2">
              <p className="mt-2 text-sm text-gray-600 uppercase line-clamp-2">{product?.product_name}</p>
              <div className="flex justify-between items-center">
                <div className="flex flex-col">
                  <span className="line-through text-gray-400 text-sm">{product?.seller?.currency_symbol || "$"}{product?.product_price}</span>
                  <span className="text-black font-semibold">{product?.seller?.currency_symbol || "$"}{product?.discount_price}</span>
                </div>
                <button className="mt-3 flex py-2 px-3 text-[14px] gap-2 items-center bg-gray-900 text-white rounded-full">
                  <IoBagHandleOutline className="text-lg" />
                  ADD TO CART
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Product View preview - matches the real buyer-facing ProductView.js layout */}
        <div>
          <p className="text-sm font-semibold text-black mb-2">Product View</p>
          <div className="bg-white border border-[rgba(195,197,217,0.6)] rounded-lg p-3">
            <img src={productImage} alt={product?.product_name} className="w-full h-40 object-contain rounded-lg mb-2" />
            <div className="flex gap-2 overflow-x-auto mb-3">
              {productImages.map((img, idx) => (
                <img key={idx} src={img.url} alt="" className="w-12 h-9 object-contain rounded border border-gray-200" />
              ))}
            </div>
            <div className="flex items-start justify-between mb-1">
              <p className="text-xs font-bold italic text-[#131b2e]">
                {product?.category?.title || "Category"} <span className="mx-1">→</span> {product?.subcategory?.title || "Subcategory"}
              </p>
              <span className="bg-[#ff8a3d] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0">Sponsored</span>
            </div>
            <p className="text-xs text-green-700 font-bold uppercase mb-1">{product?.brand_name}</p>
            <h3 className="text-base font-bold mb-1">{product?.product_name}</h3>
            <div className="flex items-center gap-4 text-xs mb-2">
              <span className="text-gray-400 line-through">{product?.seller?.currency_symbol || "$"}{product?.product_price}</span>
              <span className="text-green-700 font-semibold">{product?.seller?.currency_symbol || "$"}{product?.discount_price}</span>
              {product?.offer_percentage ? <span className="text-green-700 font-bold">{product.offer_percentage}% OFF</span> : null}
            </div>
            <button className="flex items-center gap-2 bg-black text-white rounded-full px-5 py-2 text-sm font-semibold">
              <IoBagHandleOutline /> BUY NOW
            </button>
          </div>
        </div>
      </div>

      {objective && (
        <div className="flex items-center gap-3 mb-4">
          <img src={objective.iconSrc} alt="" className="w-10 h-10" />
          <p className="font-semibold text-[#131b2e]">{objective.label}</p>
        </div>
      )}

      <label className="block text-sm font-semibold text-black mb-1">Ad Set name</label>
      <p className="text-[#434656] mb-6">{formData.adSetName}</p>

      {/* Target & Ad placement recap */}
      <div className="border border-[rgba(195,197,217,0.6)] rounded-2xl p-4 mb-4">
        <div className="flex items-center justify-between mb-4">
          <p className="font-semibold text-black">Target &amp; Ad placement</p>
          <ChevronButton open={targetingOpen} onClick={() => setTargetingOpen((o) => !o)} />
        </div>
        {targetingOpen && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#434656]">Gender</span>
              <span className="text-sm font-semibold capitalize">{formData.gender}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#434656]">Audience Age</span>
              <span className="text-sm font-semibold">{formData.minAge}-{formData.maxAge} Years</span>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-sm text-[#434656]">Interests</span>
              <div className="flex gap-2 flex-wrap">
                {formData.interests.length ? formData.interests.map((interest) => (
                  <span key={interest.id} className="border border-[#434656] rounded-full px-3 py-1 text-xs">{interest.label}</span>
                )) : <span className="text-xs text-[#9a9a9a]">None</span>}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#434656]">Device &amp; Tech</span>
              <span className="text-sm font-semibold capitalize">{formData.devices}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#434656]">Connection to Seller</span>
              <span className="text-sm font-semibold">{formData.connectToSeller ? "On" : "Off"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#434656]">Previous Brand Buyers</span>
              <span className="text-sm font-semibold">{formData.previousBrandBuyers ? "On" : "Off"}</span>
            </div>
            <div className="flex flex-col gap-2 pt-2">
              {selectedPlacements.map((label) => (
                <div key={label} className="bg-[#fefdeb] border border-[rgba(195,197,217,0.6)] rounded-xl px-4 py-2 text-sm font-semibold text-[#131b2e]">
                  {label}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Budget Allocation recap */}
      <div className="border border-[rgba(195,197,217,0.6)] rounded-2xl p-4 mb-8">
        <div className="flex items-center justify-between mb-4">
          <p className="font-semibold text-black">Budget Allocation</p>
          <ChevronButton open={budgetOpen} onClick={() => setBudgetOpen((o) => !o)} />
        </div>
        {budgetOpen && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-[#434656]">Budget type</span>
              <span className="bg-[#095228] text-white text-xs font-semibold px-3 py-1 rounded-full capitalize">
                {formData.budgetType} Budget
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div>
                <p className="text-xs text-[#747474]">Total Budget</p>
                <p className="font-semibold">$ {totalBudget.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-[#747474]">Daily Budget</p>
                <p className="font-semibold">$ {dailyBudget.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-[#747474]">Duration</p>
                <p className="font-semibold">{days} Days</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#434656]">Schedule</span>
              <span className="text-sm font-semibold">{formData.startDate} - {formData.endDate}</span>
            </div>
          </div>
        )}
      </div>

      {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="border border-[#c3c5d9] text-[#434656] font-medium text-lg px-8 py-3 rounded-full disabled:opacity-50"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="flex-1 bg-[#095228] text-white font-semibold py-3 rounded-full disabled:opacity-50"
        >
          {submitting ? "Submitting..." : "Submit for Review"}
        </button>
      </div>
    </div>
  );
}
